import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Save, MessageSquare, RefreshCw, Facebook } from 'lucide-react';

const s = {
  page: { maxWidth: 800 },
  h1: { fontSize: 22, fontWeight: 700, color: '#212529', marginBottom: 4 },
  sub: { fontSize: 13, color: '#6c757d', marginBottom: 28 },
  card: { background: '#ffffff', border: '1px solid #e9ecef', borderRadius: 12, padding: 24, marginBottom: 20 },
  cardTitle: { fontSize: 14, fontWeight: 700, color: '#a78bf8', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 },
  label: { fontSize: 12, color: '#6c757d', marginBottom: 6, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  textarea: { width: '100%', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: '10px 12px', color: '#212529', fontSize: 14, boxSizing: 'border-box', marginBottom: 8, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 },
  row: { display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 },
  btnPrimary: { display: 'flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#7c6af5,#c084fc)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { display: 'flex', alignItems: 'center', gap: 6, background: '#e9ecef', color: '#a78bf8', border: '1px solid #dee2e6', borderRadius: 8, padding: '10px 16px', fontSize: 14, cursor: 'pointer' },
  vars: { background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: '10px 14px', marginBottom: 16 },
  varRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 },
  varTag: { background: '#7c6af520', color: '#a78bf8', border: '1px solid #7c6af540', borderRadius: 6, padding: '2px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'monospace' },
  preview: { background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#495057', whiteSpace: 'pre-wrap', lineHeight: 1.7, marginBottom: 16 },
};

const VARIABLES = ['{customer_name}', '{items}', '{total}', '{cart_link}', '{order_number}'];

export default function Settings() {
  const [template, setTemplate] = useState({ id: null, name: '', body_en: '', body_kh: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('en');

  useEffect(() => { loadTemplate(); }, []);

  async function loadTemplate() {
    setLoading(true);
    try {
      const res = await api.get('/settings/dm-template');
      if (res.data) setTemplate(res.data);
    } catch {
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  }

  async function saveTemplate() {
    setSaving(true);
    try {
      await api.post('/settings/dm-template', template);
      toast.success('Template saved!');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }

  function insertVar(v) {
    const field = activeTab === 'en' ? 'body_en' : 'body_kh';
    setTemplate(t => ({ ...t, [field]: (t[field] || '') + v }));
  }

  function previewText(body) {
    return (body || '')
      .replace('{customer_name}', 'Sokha')
      .replace('{items}', 'Code 01 x2')
      .replace('{total}', '$25.00')
      .replace('{cart_link}', 'https://123cambodia.newgads.com/c/abc123')
      .replace('{order_number}', 'ORD-20260606-0001');
  }

  if (loading) return <div style={{ color: '#6c757d', padding: 40 }}>Loading...</div>;

  return (
    <div style={s.page}>
      <div style={s.h1}>⚙️ Settings</div>
      <div style={s.sub}>Manage DM templates and business configuration</div>

      {/* Facebook Connection Card */}
      <div style={s.card}>
        <div style={s.cardTitle}><MessageSquare size={16} /> Facebook Pages Connection</div>
        <p style={{ fontSize: 13, color: '#6c757d', marginBottom: 16 }}>Connect your Facebook account to enable comment fetching and auto-DM. Tokens refresh automatically each time you reconnect.</p>
        <a href="https://123cambodia.newgads.com/api/auth/fb-login" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#1877f2', color: '#fff', textDecoration: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600 }}>
          📘 Connect Facebook Account
        </a>
        {window.location.search.includes('fb=success') && <div style={{ marginTop: 12, color: '#22c55e', fontWeight: 600 }}>✅ Facebook connected successfully! Tokens updated.</div>}
        {window.location.search.includes('fb=error') && <div style={{ marginTop: 12, color: '#ef4444', fontWeight: 600 }}>❌ Connection failed. Please try again.</div>}
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}><MessageSquare size={16} /> Auto-DM Template (After Bulk Checkout)</div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['en', 'kh'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '6px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: activeTab === tab ? '#7c6af5' : '#e9ecef',
              color: activeTab === tab ? '#fff' : '#6c757d'
            }}>{tab === 'en' ? 'English' : 'ខ្មែរ'}</button>
          ))}
        </div>

        <div style={s.vars}>
          <div style={{ fontSize: 12, color: '#6c757d' }}>Click to insert variable:</div>
          <div style={s.varRow}>
            {VARIABLES.map(v => (
              <span key={v} style={s.varTag} onClick={() => insertVar(v)}>{v}</span>
            ))}
          </div>
        </div>

        {activeTab === 'en' && (
          <>
            <label style={s.label}>Message (English)</label>
            <textarea
              rows={7}
              style={s.textarea}
              value={template.body_en || ''}
              onChange={e => setTemplate(t => ({ ...t, body_en: e.target.value }))}
              placeholder="Type your English DM message..."
            />
          </>
        )}

        {activeTab === 'kh' && (
          <>
            <label style={s.label}>Message (ខ្មែរ)</label>
            <textarea
              rows={7}
              style={s.textarea}
              value={template.body_kh || ''}
              onChange={e => setTemplate(t => ({ ...t, body_kh: e.target.value }))}
              placeholder="សរសេរសារ DM ជាភាសាខ្មែរ..."
            />
          </>
        )}

        <label style={s.label}>Preview</label>
        <div style={s.preview}>
          {previewText(activeTab === 'en' ? template.body_en : template.body_kh) || '(empty)'}
        </div>

        <div style={s.row}>
          <button style={s.btnSecondary} onClick={loadTemplate}>
            <RefreshCw size={14} /> Reset
          </button>
          <button style={s.btnPrimary} onClick={saveTemplate} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>
    </div>
  );
}
